import { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import { config } from '../config';
import { ensureAppFlagsLoaded, getFlagUrl } from '../utils/flags';

let pollerInterval: number | null = null;
let pollerTimeout: number | null = null;

const formatKopecks = (amountMinor: number, currency?: string) => {
  const sign = amountMinor < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(amountMinor || 0));
  const rub = Math.floor(abs / 100);
  const kop = abs % 100;
  const suffix = currency === '' ? '' : ` ${currency || 'руб.'}`;
  return `${sign}${rub}.${String(kop).padStart(2, '0')}${suffix}`;
};

const ensureHttps = (url?: string) => (url ? url.replace('http://', 'https://') : undefined);

const buildAppIcon = (appId?: string) =>
  appId ? `/api/v1/notifications/app-icon/${appId}` : undefined;

const buildDirectLogo = (appId?: string) =>
  appId ? `https://app.rewardguru.limited/api-5.0/app_logo?app_id=${appId}&img_size=Logo2` : undefined;

export const playSoundWithFallback = async (): Promise<void> => {
  const bundled = new URL('../assets/notify.mp3', import.meta.url).href;
  const configured = config.getNotificationSoundUrl();
  const candidates = [bundled, configured];
  for (const url of candidates) {
    try {
      const audio = new Audio(url);
      audio.volume = 1;
      await audio.play();
      return;
    } catch {}
  }
  try {
    const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 880;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    o.start();
    setTimeout(() => {
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      o.stop(ctx.currentTime + 0.25);
    }, 200);
  } catch {}
};

// getDelayToNextMark был удалён: опрос выполняет только сервер

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMiniApp, setIsMiniApp] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
  });
  const iconCacheRef = useRef<Map<string, string>>(new Map());
  const scheduleRef = useRef<{ interval: number; marks: number[]; soundEnabled: boolean; webToastEnabled: boolean }>({ interval: 5, marks: [], soundEnabled: true, webToastEnabled: true });
  // isPollingRef удалён: опрос на клиенте не используется
  const audioUnlockedRef = useRef<boolean>(false);
  const recentRef = useRef<Map<string, number>>(new Map());
  const sseConnectedRef = useRef<boolean>(false);
  const [sseActive, setSseActive] = useState<boolean>(false);
  const pageIsVisible = () => {
    try { return typeof document !== 'undefined' && document.visibilityState === 'visible'; } catch { return true; }
  };
  // ref для возможного троттлинга звука (резерв)
  // const lastSoundAtRef = useRef<number>(0);

  useEffect(() => {
    // Подтягиваем карту app_id -> country_code, чтобы флаги работали без правки кода
    ensureAppFlagsLoaded().catch(() => {});
  }, []);

  // вспомогательная функция троттлинга звука может использоваться при необходимости

  const clearPolling = () => {
    try { if (pollerTimeout) window.clearTimeout(pollerTimeout); } catch {}
    pollerTimeout = null;
    try { if (pollerInterval) window.clearInterval(pollerInterval); } catch {}
    pollerInterval = null;
  };

  // Разблокировка аудио по первому жесту пользователя (иначе первый звук может быть заглушён политикой autoplay)
  useEffect(() => {
    const unlock = async () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      try {
        const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          try { if (ctx.state === 'suspended' && ctx.resume) await ctx.resume(); } catch {}
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          g.gain.value = 0.0001; // неслышимый щелчок
          o.connect(g);
          g.connect(ctx.destination);
          o.start();
          o.stop(ctx.currentTime + 0.01);
        } else {
          const a = new Audio();
          (a as any).muted = true;
          try { await a.play(); } catch {}
        }
      } catch {}
      finally {
        document.removeEventListener('pointerdown', unlock);
        document.removeEventListener('keydown', unlock);
        document.removeEventListener('touchstart', unlock as any);
      }
    };
    document.addEventListener('pointerdown', unlock, { passive: true });
    document.addEventListener('keydown', unlock, { passive: true } as any);
    document.addEventListener('touchstart', unlock as any, { passive: true } as any);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock as any);
    };
  }, []);

  // Поддержка Telegram Mini Apps: расширяем viewport
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (tg) {
      setIsMiniApp(true);
      try { tg.ready && tg.ready(); } catch {}
      try { tg.expand && tg.expand(); } catch {}
    }

    const mq = window.matchMedia ? window.matchMedia('(min-width: 768px)') : null;
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    if (mq && mq.addEventListener) mq.addEventListener('change', onChange);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
    };
  }, []);

  // Подписка на серверные события (SSE) для синхронизации уведомлений между всеми клиентами
  useEffect(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const base = config.getApiUrl(); // '/api/v1' или абсолютный
      const prefix = base.startsWith('http') ? base : (window.location.origin + base);
      const url = `${prefix.replace(/\/$/, '')}/notifications/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      es.addEventListener('open', async () => {
        sseConnectedRef.current = true;
        setSseActive(true);
        clearPolling();
        // Быстрый догон: подтянем последние непрочитанные из истории (сервер уже их сохранил)
        try {
          const res = await apiService.getServerNotificationHistory(5);
          const items = Array.isArray(res?.items) ? res.items : [];
          let anyShownLocal = false;
          for (const it of items) {
            if (it.read) continue;
            const minuteSlot = Math.floor((it.timestamp || Date.now()) / 60000);
            const alias = `${it.app_id || 'unknown'}|${it.devices_count}|${Math.round(it.total_price_minor || 0)}|${minuteSlot}`;
            const idKey = `id:${it.id}`;
            const nowTs = Date.now();
            const lastTs = recentRef.current.get(idKey) || recentRef.current.get(alias) || 0;
            // Порог 60с, чтобы не продублировать только что показанные
            if (nowTs - lastTs < 60000) continue;
            // Не показываем тосты при догоне пропущенных уведомлений
            recentRef.current.set(idKey, nowTs);
            recentRef.current.set(alias, nowTs);
            anyShownLocal = true;
          }
          if (anyShownLocal) {
            try { window.dispatchEvent(new Event('notification-history-updated')); } catch {}
            try { window.dispatchEvent(new Event('notification-unread-updated')); } catch {}
          }
        } catch {}
      });
      es.addEventListener('error', () => {
        sseConnectedRef.current = false; setSseActive(false);
        // Автопереподключение SSE, чтобы второй клиент не "замирал"
        setTimeout(() => {
          try {
            const es2 = new EventSource(url);
            es2.addEventListener('open', async () => {
              sseConnectedRef.current = true; setSseActive(true); clearPolling();
              // Лёгкий догон через историю
              try {
                const res2 = await apiService.getServerNotificationHistory(5);
                const items2 = Array.isArray(res2?.items) ? res2.items : [];
                let shown = false;
                for (const it of items2) {
                  const ts = Number(it.timestamp || Date.now());
                  if (Date.now() - ts > 90_000) continue;
                  const minuteSlot = Math.floor(ts / 60000);
                  const alias = `${it.app_id || 'unknown'}|${it.devices_count}|${Math.round(it.total_price_minor || 0)}|${minuteSlot}`;
                  const idKey = `id:${it.id}`;
                  const lastTs = recentRef.current.get(idKey) || recentRef.current.get(alias) || 0;
                  if (Date.now() - lastTs < 60000) continue;
                  // Не показываем тосты при догоне пропущенных уведомлений
                  recentRef.current.set(idKey, Date.now());
                  recentRef.current.set(alias, Date.now());
                  shown = true;
                }
                if (shown) {
                  try { window.dispatchEvent(new Event('notification-history-updated')); } catch {}
                  try { window.dispatchEvent(new Event('notification-unread-updated')); } catch {}
                }
              } catch {}
            });
            es2.addEventListener('message', (ev: any) => es.dispatchEvent(new MessageEvent('message', { data: (ev as MessageEvent).data })));
          } catch {}
        }, 2000);
      });
      es.addEventListener('message', async (ev: MessageEvent) => {
        try {
          if (!ev?.data) return;
          const payload = JSON.parse(ev.data);
          if (payload?.type !== 'orders' || !Array.isArray(payload?.groups)) return;
          const groups = payload.groups as any[];
          let anyShown = false;
          for (const g of groups) {
            // Уникальный ключ на основе исходных данных + тайм-слота (минуты)
            const id = String(g.id || '');
            const minuteSlot = Math.floor((g.timestamp ? Number(g.timestamp) : Date.now()) / 60000);
            const alias = `${g.app_id || 'unknown'}|${g.devices_count}|${Math.round(g.total_price || 0)}|${minuteSlot}`;
            const k = id ? `id:${id}` : alias;
            const nowTs = Date.now();
            const lastTs = recentRef.current.get(k) || recentRef.current.get(alias) || 0;
            if (nowTs - lastTs < 60000) continue; // окно 60с для потоковых дублей

            const title = '🔥 Новый заказ!';
            const formatted = formatKopecks(Math.round(g.total_price || 0), '');
            const flagUrl = getFlagUrl(g.app_id);
            const titleWithFlag = title;
            const body = `Количество: ${g.devices_count}\nОбщая сумма: ${formatted} 💰`;
            const imgUrl = ensureHttps(g.sample_pic) || buildDirectLogo(g.app_id) || buildAppIcon(g.app_id);
            const iconUrl = await getIconObjectUrl(imgUrl, g.app_id || imgUrl, g.app_id);

            // Локальный показ на каждом клиенте
            if (!isMiniApp && "Notification" in window) {
              if (Notification.permission === 'granted') {
                new Notification(titleWithFlag, { body, icon: iconUrl });
              } else if (Notification.permission !== 'denied') {
                Notification.requestPermission();
              }
            }
            if (scheduleRef.current.webToastEnabled && pageIsVisible()) {
              toast.custom(
                <div className="flex items-center p-3 bg-[#23262F] border border-[#353945] rounded-lg shadow-lg max-w-sm">
                  {iconUrl ? (
                    <img src={iconUrl} alt="app" className="w-12 h-12 rounded mr-3 object-contain" />
                  ) : null}
                  <div className="text-sm whitespace-pre-line">
                    <div className="text-white font-semibold flex items-center gap-2">
                      <span>{titleWithFlag}</span>
                      {flagUrl ? <img src={flagUrl} alt="flag" className="w-4 h-3 rounded-sm" /> : null}
                    </div>
                    <div className="text-[#B1B5C3]">{body}</div>
                  </div>
                </div>,
                { duration: 6000, id: `ord-${alias}` }
              );
              if (scheduleRef.current.soundEnabled) { await playSoundWithFallback(); }
            }

            recentRef.current.set(k, nowTs);
            recentRef.current.set(alias, nowTs);
            anyShown = true;
          }
          if (anyShown) {
            try { window.dispatchEvent(new Event('notification-history-updated')); } catch {}
            try { window.dispatchEvent(new Event('notification-unread-updated')); } catch {}
          }
        } catch {}
      });
      return () => {
        try { es.close(); } catch {}
        sseConnectedRef.current = false;
        setSseActive(false);
      };
    } catch {}
  }, [isMiniApp]);

  const getIconObjectUrl = async (rawUrl?: string, cacheKey?: string, appId?: string) => {
    const url = rawUrl || buildDirectLogo(appId);
    if (!url) return undefined;
    const key = cacheKey || url;
    const cached = iconCacheRef.current.get(key);
    if (cached) return cached;
    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('access_token');
      if (token && url.startsWith('/')) headers['Authorization'] = `Bearer ${token}`;
      let resp = await fetch(url, { cache: 'no-cache', headers });
      if (!resp.ok && appId) {
        const direct = buildDirectLogo(appId)!;
        resp = await fetch(direct, { cache: 'no-cache' });
      }
      if (!resp.ok) return url;
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      iconCacheRef.current.set(key, objUrl);
      return objUrl;
    } catch {
      return url;
    }
  };

  // Клиентский опрос полностью отключён: показ идёт только по SSE
  useEffect(() => { clearPolling(); }, [sseActive]);
  return (
    <div className="dark bg-[#141416] min-h-screen flex">
      {/* Фоновая прокладка для safe-area, чтобы контент не просвечивал над хедером */}
      {isMiniApp ? (
        <div
          className="fixed left-0 right-0 z-30 bg-[#181A20] md:hidden"
          style={{ top: 0, height: 'calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' }}
        />
      ) : null}

      {/* Мобильная верхняя панель */}
      <div
        className="fixed left-0 right-0 z-40 flex items-center justify-between bg-[#181A20] border-b border-[#23262F] h-16 px-4 md:hidden"
        style={isMiniApp ? { top: 'calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' } : { top: 0 }}
      >
        <button
          className="text-white p-2 rounded-lg bg-[#23262F] border border-[#353945]"
          onClick={() => setMobileOpen(true)}
          aria-label="Открыть меню"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path fillRule="evenodd" d="M3.75 5.25A.75.75 0 0 1 4.5 4.5h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 7A.75.75 0 0 1 4.5 11.5h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 7a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" /></svg>
        </button>
        <div className="text-white font-bold text-lg">GuruApp Hub</div>
        <div className="w-10" />
      </div>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main
        className={`flex-1 ${collapsed ? 'md:ml-20' : 'md:ml-64'} ml-0 min-h-screen bg-[#181A20] text-white transition-all duration-300 md:pt-0 pt-16`}
        style={isMiniApp && !isDesktop ? { paddingTop: 'calc(64px + env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' } : undefined}
      >
        {children}
      </main>
    </div>
  );
};

export default Layout; 